import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useDuplicatePurchaseOrder } from '@/services/purchaseOrders/useDuplicatePurchaseOrder';
import { usePurchaseOrder } from '@/services/purchaseOrders/usePurchaseOrders';
import { FunctionComponent, Suspense, useState } from 'react';
import { useParams } from 'react-router';

const DuplicatePurchaseOrderModal: FunctionComponent<ModalProps<{ purchaseOrderId: string }>> = props => {
  const DuplicatePurchaseOrder = () => {
    const orderId = props.props?.purchaseOrderId;
    const { partnerId, companyId } = useParams();

    if (!(partnerId && companyId && orderId)) throw new Error('Missing params');

    const order = usePurchaseOrder(orderId);

    if (!order) throw new Error(`Order - ${orderId} not found`);

    const duplicatePurchaseOrder = useDuplicatePurchaseOrder(partnerId, companyId, orderId);

    const [loading, setLoading] = useState(false);
    const [newName, setNewName] = useState<string>(`${order.name} - Copy`);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const onDuplicate = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        await duplicatePurchaseOrder(newName);
        props.close();
      } catch (error) {
        setErrorMessage('Unable to duplicate order.');
      }

      setLoading(false);
    };

    return (
      <Card className="w-full p-8 space-y-2">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-bold">Duplicate</h1>
        </Modal.Controls>

        <div className="space-y-6">
          <div className="space-y-2">
            <p>
              Please enter a name for the copy of <span className="font-semibold">{order.name}.</span>
            </p>
            <TextInput value={newName} onChange={setNewName} id={'newName'} />
            {errorMessage && <p className="text-red-500">{errorMessage}</p>}
          </div>

          <div className="flex gap-2 justify-between">
            <Button onClick={props.close}>Cancel</Button>
            <Button type="button" variant={ButtonVariant.Primary} loading={loading} onClick={onDuplicate}>
              Create
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <DuplicatePurchaseOrder />
      </Suspense>
    </Modal>
  );
};

export default DuplicatePurchaseOrderModal;
